import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Plus, FileText, Pencil, Trash2 } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { FormField } from "../components/ui/FormField/FormField";
import { Select } from "../components/ui/Select/Select";
import { Modal } from "../components/ui/Modal/Modal";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { EmptyState } from "../components/ui/EmptyState/EmptyState";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import {
	useLeadList,
	useLeadDetail,
	useCreateLead,
	useUpdateLead,
	useDeleteLead,
	type Lead,
	type CreateLeadInput,
} from "../hooks/use-leads";
import {
	useSapCountries,
	useSapStates,
	useSapBpGroups,
	useSapCardsByTax,
	type SapCardByTax,
} from "../hooks/use-companies";
import { CreateCompanyModal, type CompanyFormValues } from "./companies";
import { isValidPhone } from "../lib/phone";
import { useAuth } from "../lib/use-auth";
import { useToast } from "../lib/use-toast";
import { hasPermission } from "../lib/permissions";
import styles from "./Leads.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/leads",
	component: withPermission(LeadsPage, "leads", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
	}),
});

// SAP muhatap kartı yalnızca bu para birimlerini kabul ediyor.
const LEAD_CURRENCY_OPTIONS = [
	{ value: "TRY", label: "TRY" },
	{ value: "USD", label: "USD" },
	{ value: "EUR", label: "EUR" },
];

// VKN 10, TCKN 11 hanedir; daha kısa değerle SAP araması anlamsız sonuç dönüyor.
const TAX_NUMBER_MIN_LENGTH = 10;

const EMPTY_LEAD_FORM = {
	email: "",
	phone: "",
	companyName: "",
	tradeName: "",
	taxNumber: "",
	taxOffice: "",
	fax: "",
	website: "",
	industry: "",
	sapGroupCode: "" as string,
	currency: "TRY",
	addressStreet: "",
	addressCity: "",
	addressState: "",
	addressPostalCode: "",
	addressCountry: "TÜRKİYE",
};

type LeadFormValues = typeof EMPTY_LEAD_FORM;

function leadToForm(lead: Lead): LeadFormValues {
	return {
		email: lead.email ?? "",
		phone: lead.phone ?? "",
		companyName: lead.companyName ?? "",
		tradeName: lead.tradeName ?? "",
		taxNumber: lead.taxNumber ?? "",
		taxOffice: lead.taxOffice ?? "",
		fax: lead.fax ?? "",
		website: lead.website ?? "",
		industry: lead.industry ?? "",
		sapGroupCode: lead.sapGroupCode !== null ? String(lead.sapGroupCode) : "",
		currency: lead.currency ?? "TRY",
		addressStreet: lead.address?.street ?? "",
		addressCity: lead.address?.city ?? "",
		addressState: lead.address?.state ?? "",
		addressPostalCode: lead.address?.postalCode ?? "",
		addressCountry: lead.address?.country || "TÜRKİYE",
	};
}


export function LeadFormModal({
	isOpen,
	onClose,
	leadId,
}: {
	isOpen: boolean;
	onClose: () => void;
	leadId?: string | undefined;
}) {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const isEdit = !!leadId;
	const detailQuery = useLeadDetail(isOpen && leadId ? leadId : "");
	const [form, setForm] = useState<LeadFormValues>(EMPTY_LEAD_FORM);
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const createLead = useCreateLead();
	const updateLead = useUpdateLead();
	const { data: countries } = useSapCountries();
	const { data: bpGroups } = useSapBpGroups();
	const selectedCountryCode = countries?.find((c) => c.name === form.addressCountry)?.code;
	const { data: states } = useSapStates(selectedCountryCode);

	const detailLead = detailQuery.data;
	useEffect(() => {
		if (!isOpen) return;
		setFormError("");
		setFieldErrors({});
		setForm(isEdit && detailLead ? leadToForm(detailLead) : EMPTY_LEAD_FORM);
	}, [isOpen, isEdit, detailLead]);

	// VKN/TCKN zorunlu değil; ama yazılırsa muhatap ekranındaki gibi mevcut SAP kartlarını
	// gösterip bilgileri doldurmayı öneriyoruz — aynı cari için ikinci kart açılmasın diye.
	const [taxToCheck, setTaxToCheck] = useState("");
	const trimmedTaxNumber = form.taxNumber.trim();
	useEffect(() => {
		if (trimmedTaxNumber.length < TAX_NUMBER_MIN_LENGTH) return;
		const timer = setTimeout(() => setTaxToCheck(trimmedTaxNumber), 400);
		return () => clearTimeout(timer);
	}, [trimmedTaxNumber]);
	const { data: taxCards, isFetching: taxLoading } = useSapCardsByTax(taxToCheck, !isEdit);
	const sapCardsByTax = !isEdit && taxToCheck === trimmedTaxNumber ? (taxCards?.items ?? []) : [];
	const [companyPrefill, setCompanyPrefill] = useState<Partial<CompanyFormValues> | null>(null);

	const fillFromSapCard = (card: SapCardByTax) => {
		setForm((prev) => ({
			...prev,
			companyName: card.cardName || prev.companyName,
			taxNumber: card.taxNumber ?? prev.taxNumber,
			phone: card.phone ?? prev.phone,
			fax: card.fax ?? prev.fax,
			email: card.email ?? prev.email,
			website: card.website ?? prev.website,
			sapGroupCode:
				card.groupCode !== undefined && card.groupCode !== null
					? String(card.groupCode)
					: prev.sapGroupCode,
			addressStreet: card.address?.street ?? prev.addressStreet,
			addressCity: card.address?.city ?? prev.addressCity,
			addressState: card.address?.state ?? prev.addressState,
			addressPostalCode: card.address?.postalCode ?? prev.addressPostalCode,
			addressCountry: card.address?.country || prev.addressCountry,
		}));
	};

	const openCompanyFromCard = (card: SapCardByTax) => {
		setCompanyPrefill({
			legalName: card.cardName || form.companyName,
			tradeName: form.tradeName || card.cardName || "",
			taxNumber: card.taxNumber ?? form.taxNumber,
			phone: card.phone ?? form.phone,
			fax: card.fax ?? form.fax,
			email: card.email ?? form.email,
			website: card.website ?? form.website,
			industry: form.industry,
			status: "customer",
			...(card.groupCode !== undefined && card.groupCode !== null
				? { sapGroupCode: card.groupCode }
				: {}),
			currency: form.currency,
			currencies: form.currency ? [form.currency] : [],
			addressStreet: card.address?.street ?? form.addressStreet,
			addressCity: card.address?.city ?? form.addressCity,
			addressState: card.address?.state ?? form.addressState,
			addressPostalCode: card.address?.postalCode ?? form.addressPostalCode,
			addressCountry: card.address?.country || form.addressCountry,
		});
		onClose();
	};

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.companyName?.trim()) errors.companyName = t("requiredField");
		if (!form.sapGroupCode) errors.sapGroupCode = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		if (form.phone && !isValidPhone(form.phone)) {
			setFormError(t("invalidPhone"));
			return;
		}
		const {
			addressStreet,
			addressCity,
			addressState,
			addressPostalCode,
			addressCountry,
			sapGroupCode,
			currency,
			...rest
		} = form;
		const trimmedRest = Object.fromEntries(
			Object.entries(rest).map(([k, v]) => [k, v.trim()]),
		) as Record<string, string>;
		const address = {
			street: addressStreet.trim() || undefined,
			city: addressCity.trim() || undefined,
			state: addressState.trim() || undefined,
			postalCode: addressPostalCode.trim() || undefined,
			country: addressCountry.trim() || undefined,
		};
		const hasAddress = addressStreet || addressCity || addressState || addressPostalCode;

		if (isEdit && leadId) {
			const payload = {
				...trimmedRest,
				sapGroupCode: Number(sapGroupCode),
				currency,
				address,
			} as Partial<CreateLeadInput>;
			updateLead.mutate(
				{ id: leadId, data: payload },
				{
					onSuccess: () => {
						onClose();
						showToast(t("updateSuccess"));
					},
					onError: (err) => {
						setFormError(err instanceof Error ? err.message : t("updateFailed"));
					},
				},
			);
			return;
		}

		const payload = {
			...(Object.fromEntries(
				Object.entries(trimmedRest).filter(([, v]) => v !== ""),
			) as unknown as CreateLeadInput),
			sapGroupCode: Number(sapGroupCode),
			currency,
			address: hasAddress ? address : undefined,
		};
		createLead.mutate(payload, {
			onSuccess: () => {
				onClose();
				setForm(EMPTY_LEAD_FORM);
				setFormError("");
				showToast(t("createSuccess"));
			},
			onError: (err) => {
				setFormError(err instanceof Error ? err.message : t("createFailed"));
			},
		});
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={isEdit ? t("editLead") : t("createLead")}
				closeOnOverlayClick={false}
			>
				{isEdit && detailQuery.isLoading ? (
					<LoadingSpinner />
				) : (
					<>
						{formError && <div className={styles.formError}>{formError}</div>}
						<FormField label={t("legalName")} required>
							<Input
								value={form.companyName}
								onChange={(e) => setForm({ ...form, companyName: e.target.value })}
								error={fieldErrors.companyName}
								autoFocus
							/>
						</FormField>
						<FormField label={t("group")} required error={fieldErrors.sapGroupCode ?? ""}>
							<Select
								value={form.sapGroupCode}
								onChange={(e) => setForm({ ...form, sapGroupCode: e.target.value })}
								options={[
									{ value: "", label: t("notAvailable") },
									...(bpGroups ?? []).map((g) => ({ value: String(g.code), label: g.name })),
								]}
							/>
						</FormField>
						<FormField label={t("currency")} required>
							<Select
								value={form.currency}
								onChange={(e) => setForm({ ...form, currency: e.target.value })}
								options={LEAD_CURRENCY_OPTIONS}
							/>
						</FormField>
						<FormField label={t("email")}>
							<Input
								type="email"
								value={form.email}
								onChange={(e) => setForm({ ...form, email: e.target.value })}
							/>
						</FormField>
						<FormField label={t("phone")}>
							<Input
								value={form.phone}
								onChange={(e) => setForm({ ...form, phone: e.target.value })}
							/>
						</FormField>
						<FormField label={t("tradeName")}>
							<Input
								value={form.tradeName}
								onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
							/>
						</FormField>
						<FormField label="Adres">
							<Input
								value={form.addressStreet}
								onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
							/>
						</FormField>
						<FormField label="Ülke">
							<Select
								value={form.addressCountry}
								onChange={(e) => {
									const country = e.target.value;
									setForm((prev) => {
										if (country && country !== "TÜRKİYE") {
											return {
												...prev,
												addressCountry: country,
												addressState: "",
												addressCity: "",
												taxNumber: prev.taxNumber.trim() || "2222222222",
											};
										}
										return {
											...prev,
											addressCountry: country,
											addressState: "",
											addressCity: "",
											taxNumber: prev.taxNumber === "2222222222" ? "" : prev.taxNumber,
										};
									});
								}}
								options={[
									{ value: "", label: t("notAvailable") },
									...(countries ?? []).map((c) => ({ value: c.name, label: c.name })),
								]}
							/>
						</FormField>
						<FormField label="İl">
							<Select
								value={form.addressState}
								onChange={(e) =>
									setForm({ ...form, addressState: e.target.value, addressCity: "" })
								}
								disabled={!form.addressCountry}
								options={[
									{ value: "", label: t("notAvailable") },
									...(states ?? []).map((s) => ({ value: s.name, label: s.name })),
								]}
							/>
						</FormField>
						<FormField label="İlçe">
							<Input
								value={form.addressCity}
								onChange={(e) => setForm({ ...form, addressCity: e.target.value })}
								disabled={!form.addressCountry}
							/>
						</FormField>
						<FormField label="Posta Kodu">
							<Input
								value={form.addressPostalCode}
								onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })}
							/>
						</FormField>
						<FormField label={t("taxNumber")}>
							<Input
								value={form.taxNumber}
								onChange={(e) => setForm({ ...form, taxNumber: e.target.value })}
							/>
							{!isEdit && taxLoading && (
								<p className={styles.similarHint}>{t("sapSimilarChecking")}</p>
							)}
							{!taxLoading && sapCardsByTax.length > 0 && (
								<div className={styles.similarBox}>
									<p className={styles.similarTitle}>{t("sapCardsByTaxFound")}</p>
									<ul className={styles.similarList}>
										{sapCardsByTax.map((card) => (
											<li key={card.sapCardCode}>
												<strong>{card.cardName}</strong>
												{card.currency ? ` · ${card.currency}` : ""} · {card.sapCardCode}{" "}
												{card.companyId ? (
													<Link to="/companies/$id" params={{ id: card.companyId }}>
														{t("viewDetails")}
													</Link>
												) : (
													<button
														type="button"
														className={styles.linkButton}
														onClick={() => fillFromSapCard(card)}
													>
														{t("fillFromSapCard")}
													</button>
												)}{" "}
												<button
													type="button"
													className={styles.linkButton}
													onClick={() => openCompanyFromCard(card)}
												>
													{t("createCompanyFromSapCard")}
												</button>
											</li>
										))}
									</ul>
									<p className={styles.similarHint}>{t("leadTaxCardExistsHint")}</p>
								</div>
							)}
						</FormField>
						<FormField label={t("taxOffice")}>
							<Input
								value={form.taxOffice}
								onChange={(e) => setForm({ ...form, taxOffice: e.target.value })}
							/>
						</FormField>
						<FormField label={t("fax")}>
							<Input
								value={form.fax}
								onChange={(e) => setForm({ ...form, fax: e.target.value })}
							/>
						</FormField>
						<FormField label={t("website")}>
							<Input
								value={form.website}
								onChange={(e) => setForm({ ...form, website: e.target.value })}
							/>
						</FormField>
						<FormField label={t("industry")}>
							<Input
								value={form.industry}
								onChange={(e) => setForm({ ...form, industry: e.target.value })}
							/>
						</FormField>
						<div className={styles.modalActions}>
							<Button variant="ghost" onClick={onClose}>
								{t("cancel")}
							</Button>
							<Button
								onClick={submit}
								isLoading={isEdit ? updateLead.isPending : createLead.isPending}
							>
								{isEdit ? t("save") : t("create")}
							</Button>
						</div>
					</>
				)}
			</Modal>

			<CreateCompanyModal
				isOpen={companyPrefill !== null}
				onClose={() => setCompanyPrefill(null)}
				{...(companyPrefill ? { initialValues: companyPrefill } : {})}
			/>
		</>
	);
}

function LeadsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: "/leads" });
	const { user } = useAuth();
	const { showToast } = useToast();
	const canUpdate = hasPermission(user, "leads", "update");
	const canDelete = hasPermission(user, "leads", "delete");
	const { page, size, status, search: searchQuery, createdAtFrom, createdAtTo } = route.useSearch();
	const setStatus = (s: string) =>
		navigate({ search: (prev) => ({ ...prev, status: s, page: 1 }) });
	const setPage = (p: number) => navigate({ search: (prev) => ({ ...prev, page: p }) });
	const setPageSize = (s: number) =>
		navigate({ search: (prev) => ({ ...prev, size: s, page: 1 }) });
	const [createOpen, setCreateOpen] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const { data, isLoading, isError, refetch } = useLeadList({
		status: status || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: size,
	});
	const deleteLead = useDeleteLead();

	const leads = data?.items ?? [];
	const total = data?.total ?? 0;

	const tabs = [
		{ key: "", label: t("all") },
		{ key: "new", label: t("new") },
		{ key: "converted", label: t("converted") },
	];

	
	const renderRowActions = (row: Lead) => (
		<span className={styles.rowActions}>
			<Link to={`/leads/${row.id}` as never} className={styles.link}>
				{t("viewDetails")}
			</Link>
			{canUpdate && (
				<button
					type="button"
					className={styles.actionBtn}
					onClick={() => setEditId(row.id)}
					aria-label={t("edit")}
				>
					{t("edit")}
				</button>
			)}
			{canDelete && (
				<button
					type="button"
					className={styles.actionBtnDanger}
					onClick={() => setDeleteId(row.id)}
					aria-label={t("delete")}
				>
					{t("delete")}
				</button>
			)}
		</span>
	);

	return (
		<div>
			<div className={styles.pageHeaderRow ?? ""}>
				<PageHeader
					title={t("leads")}
					action={
						<Button className={styles.createButton ?? ""} onClick={() => setCreateOpen(true)}>
							<Plus size={16} style={{ marginRight: 6 }} />
							<span className={styles.fullLabel ?? ""}>{t("createLead")}</span>
							<span className={styles.shortLabel ?? ""}>{t("create")}</span>
						</Button>
					}
				/>
			</div>

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{tabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								setStatus(tab.key);
								setPage(1);
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
				<div className={styles.filterBarCompact ?? ""}>
					<FilterBar
						filters={[
							{
								key: "createdAt",
								type: "dateRange",
								label: t("createdAt"),
								placeholder: t("date"),
							},
							{ key: "search", type: "text", label: t("search") },
						]}
						values={{ search: searchQuery, createdAtFrom, createdAtTo }}
						onChange={(vals) => {
							navigate({
								search: (prev) => ({
									...prev,
									search: vals.search || "",
									createdAtFrom: vals.createdAtFrom || "",
									createdAtTo: vals.createdAtTo || "",
									page: 1,
								}),
							});
						}}
					/>
				</div>
			</div>

			<Card bodyClassName={styles.listCardBody ?? ""}>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<>
					<div className={styles.desktopTable ?? ""}>
					<Table
						className={styles.compactTable ?? ""}
						columns={[
							{ key: "companyName", header: t("legalName") },
							{ key: "email", header: t("email") },
							{ key: "phone", header: t("phone") },
							{
								key: "status",
								header: t("status"),
								render: (row) => (
									<span className={styles.statusCell}>
										<StatusBadge status={row.status} type="lead" />
									</span>
								),
							},
							...(canUpdate
								? [
										{
											key: "owner",
											header: t("salesPerson"),
											render: (row: Lead) =>
												row.owner
													? [row.owner.firstName, row.owner.lastName].filter(Boolean).join(" ")
													: "—",
										},
									]
								: []),
							{
								key: "actions",
								header: t("actions"),
								render: renderRowActions,
							},
						]}
						data={leads}
						keyExtractor={(row) => row.id}
					/>
					</div>

					<div className={styles.mobileList ?? ""}>
						{leads.length === 0 && <EmptyState />}
						{leads.map((row) => (
							<div key={row.id} className={styles.mobileCard}>
								<div className={styles.mobileCardName}>{row.companyName}</div>
								<div className={styles.mobileCardSub}>
									{[row.email, row.phone].filter(Boolean).join(" · ")}
								</div>
								{canUpdate && row.owner && (
									<div className={styles.mobileCardMeta}>
										{[row.owner.firstName, row.owner.lastName].filter(Boolean).join(" ")}
									</div>
								)}
								<div className={styles.mobileCardBottom}>
									<span className={styles.statusCell}>
										<StatusBadge status={row.status} type="lead" />
									</span>
									<span className={styles.mobileCardActions}>
										<Link
											to={`/leads/${row.id}` as never}
											className={styles.mobileIconBtn}
											aria-label={t("viewDetails")}
										>
											<FileText size={15} />
										</Link>
										{canUpdate && (
											<button
												type="button"
												className={styles.mobileIconBtn}
												onClick={() => setEditId(row.id)}
												aria-label={t("edit")}
											>
												<Pencil size={15} />
											</button>
										)}
										{canDelete && (
											<button
												type="button"
												className={styles.mobileIconBtnDanger}
												onClick={() => setDeleteId(row.id)}
												aria-label={t("delete")}
											>
												<Trash2 size={15} />
											</button>
										)}
									</span>
								</div>
							</div>
						))}
					</div>
					</>
				)}
				<Pagination
					page={page}
					pageSize={size}
					total={total}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</Card>

			<LeadFormModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
			<LeadFormModal
				isOpen={editId !== null}
				onClose={() => setEditId(null)}
				leadId={editId ?? undefined}
			/>

			<ConfirmDialog
				isOpen={deleteId !== null}
				onClose={() => setDeleteId(null)}
				onConfirm={() => {
					if (!deleteId) return;
					deleteLead.mutate(deleteId, {
						onSuccess: () => {
							setDeleteId(null);
							showToast(t("leadDeleteSuccess"));
						},
					});
				}}
				message={t("confirmDelete")}
				isLoading={deleteLead.isPending}
			/>
		</div>
	);
}
